import * as React from 'react';
import { Form, FormGroup, FormSelect, FormSelectOption, Label, Title } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { aggregate } from '../../../../shared/riskregister/aggregate';
import { relaxedBase } from '../../../../shared/riskregister/filters';
import { groupLabel, unitValue } from '../../../../shared/riskregister/grouping';
import { type MonthStat, monthIndex, monthStat } from '../../../../shared/riskregister/monthly';
import type { GroupDimension, Payload, Row, Tier, ViewState } from '../../../../shared/riskregister/types';

const MONTHLY_DIMENSIONS: Array<{ value: GroupDimension; label: string }> = [
  { value: 'manager', label: 'Team / manager' },
  { value: 'assignee', label: 'Owner' },
  { value: 'svp', label: 'Platform area' },
  { value: 'epic', label: 'Initiative' },
  { value: 'project', label: 'Project' },
];

interface MonthlyGroup {
  unit: string;
  rows: Row[];
  stat: MonthStat;
}

const Sparkline: React.FunctionComponent<{ values: number[] }> = ({ values }) => {
  const maximum = Math.max(...values, 1);
  return (
    <span
      role="img"
      aria-label={`Monthly open volume: ${values.join(', ')}`}
      style={{ display: 'inline-flex', alignItems: 'flex-end', gap: '1px', height: '1.5rem', minWidth: '3rem' }}
    >
      {values.map((value, index) => (
        <span
          aria-hidden="true"
          key={`${index}-${value}`}
          style={{
            display: 'block',
            width: '0.22rem',
            height: `${Math.max((value / maximum) * 100, value ? 8 : 2)}%`,
            backgroundColor: 'var(--pf-v5-global--info-color--100)',
          }}
        />
      ))}
    </span>
  );
};

const TierBar: React.FunctionComponent<{ payload: Payload; tiers: Record<Tier, number> }> = ({ payload, tiers }) => {
  const total = payload.tier_order.reduce((sum, tier) => sum + tiers[tier], 0);
  const description = payload.tier_order.map((tier) => `${payload.tiers[tier].short}: ${tiers[tier]}`).join(', ');
  return (
    <div>
      <span
        role="img"
        aria-label={`Tier split as of today. ${description}`}
        style={{
          display: 'flex',
          overflow: 'hidden',
          minWidth: '7rem',
          minHeight: '0.85rem',
          borderRadius: '3px',
          backgroundColor: 'var(--pf-v5-global--BackgroundColor--200)',
        }}
      >
        {payload.tier_order
          .filter((tier) => tiers[tier] > 0)
          .map((tier) => (
            <span
              aria-hidden="true"
              key={tier}
              style={{
                width: `${(tiers[tier] / Math.max(total, 1)) * 100}%`,
                minWidth: '0.2rem',
                backgroundColor: payload.tiers[tier].color,
              }}
            />
          ))}
      </span>
      <span style={{ display: 'block', marginTop: '0.25rem' }}>
        {payload.tier_order.map((tier) => `${payload.tiers[tier].short}: ${tiers[tier]}`).join(', ')}
      </span>
    </div>
  );
};

export const MonthlyTable: React.FunctionComponent<{
  payload: Payload;
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
}> = ({ payload, state, onChange }) => {
  const rows = relaxedBase(payload.rows, state, payload);
  const index = monthIndex(payload.months, state.month);
  const selectedMonth = payload.months[index];
  const total = monthStat(rows, index, payload.mbits);
  const groups = new Map<string, Row[]>();
  rows.forEach((row) => {
    const unit = unitValue(row, state.monthby, payload);
    const group = groups.get(unit);
    if (group) group.push(row);
    else groups.set(unit, [row]);
  });
  const monthlyGroups: MonthlyGroup[] = Array.from(groups, ([unit, groupRows]) => ({
    unit,
    rows: groupRows,
    stat: monthStat(groupRows, index, payload.mbits),
  })).sort(
    (left, right) =>
      right.stat.over - left.stat.over || right.stat.vol - left.stat.vol || left.unit.localeCompare(right.unit),
  );

  return (
    <div className="risk-register__panel-stack">
      <Form aria-label="Monthly view controls" className="risk-register__form-grid">
        <FormGroup label="Month" fieldId="risk-register-month">
          <FormSelect id="risk-register-month" value={String(index)} onChange={(_event, month) => onChange({ month })}>
            {payload.months.map((month, monthIndexValue) => (
              <FormSelectOption key={month} value={String(monthIndexValue)} label={month} />
            ))}
          </FormSelect>
        </FormGroup>
        <FormGroup label="View by" fieldId="risk-register-month-by">
          <FormSelect
            id="risk-register-month-by"
            value={state.monthby}
            onChange={(_event, monthby) => onChange({ monthby: monthby as GroupDimension })}
          >
            {MONTHLY_DIMENSIONS.map((option) => (
              <FormSelectOption key={option.value} value={option.value} label={option.label} />
            ))}
          </FormSelect>
        </FormGroup>
      </Form>
      <p className="risk-register__panel-summary">
        At <strong>{selectedMonth}</strong>: {total.vol} open of {total.reg} registered, {total.over} overdue, and{' '}
        {total.paid} paid down during the month.
        {total.unknown > 0 && (
          <>
            {' '}
            {total.unknown} item{total.unknown === 1 ? '' : 's'} had an unknown reconstructed state.
          </>
        )}
        {total.dueUnknown > 0 && (
          <>
            {' '}
            {total.dueUnknown} item{total.dueUnknown === 1 ? '' : 's'} had an unknown due date.
          </>
        )}
      </p>
      <div className="risk-register__chip-row" aria-label="Risk tier legend">
        {payload.tier_order.map((tier) => (
          <Label
            key={tier}
            color={tier === 'critical' ? 'red' : tier === 'high' ? 'orange' : tier === 'moderate' ? 'blue' : 'grey'}
            isCompact
          >
            {payload.tiers[tier].label}
          </Label>
        ))}
      </div>
      <div className="risk-register__table-wrap">
        <Table aria-label="Monthly Risk Register view by unit" variant="compact">
          <Thead>
            <Tr>
              <Th scope="col">{MONTHLY_DIMENSIONS.find((option) => option.value === state.monthby)?.label}</Th>
              <Th scope="col">Volume at month end</Th>
              <Th scope="col">Tier split today</Th>
              <Th scope="col">Overdue then</Th>
              <Th scope="col">Paydown in month</Th>
              <Th scope="col">Volume trend</Th>
            </Tr>
          </Thead>
          <Tbody>
            {monthlyGroups.map((group) => {
              const current = aggregate(group.rows, payload);
              const series = payload.months.map(
                (_month, seriesIndex) => monthStat(group.rows, seriesIndex, payload.mbits).vol,
              );
              return (
                <Tr key={group.unit}>
                  <Td dataLabel="Unit">{groupLabel(group.unit, state.monthby, payload)}</Td>
                  <Td dataLabel="Volume at month end">{group.stat.vol}</Td>
                  <Td dataLabel="Tier split today">
                    <TierBar payload={payload} tiers={current.tiers} />
                  </Td>
                  <Td dataLabel="Overdue then">{group.stat.over}</Td>
                  <Td dataLabel="Paydown in month">{group.stat.paid}</Td>
                  <Td dataLabel="Volume trend">
                    <Sparkline values={series} />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </div>
      <div className="risk-register__callout risk-register__callout--info">
        <Title headingLevel="h3" size="md">
          Reconstruction caveats
        </Title>
        <p>
          Month-end volume, overdue, and paydown are reconstructed from item history; they are not today&apos;s figures
          relabelled.
        </p>
        <p className="risk-register__callout-copy">
          The tier split is as of today only and is not backdated. The month selector covers the last 12 months,
          or the life of the register if it is younger than that.
        </p>
      </div>
    </div>
  );
};
